<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i>  {{ $t('scheduled-tournament.page.title') }}
      <template v-slot:right>
        <b-button @click="openModal('create')" size="sm" variant="success">  {{ $t('scheduled-tournament.btn.create_room') }}</b-button>
      </template>
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('title.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="phrase"
                  :label="$t('scheduled-tournament.filters.label.phrase')"
                  :placeholder="$t('scheduled-tournament.filters.placeholder.phrase')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="walletCurrency"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="walletCurrency"
                  :label="$t('scheduled-tournament.filters.label.wallet_currency')"
                  :placeholder="$t('scheduled-tournament.filters.placeholder.wallet_currency')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('scheduled-tournament.filters.label.status')"
                  :placeholder="$t('scheduled-tournament.filters.placeholder.status')">
              </filter-select>
            </div>
          </div> <!-- #row -->

          <div class="row">
            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="buyIn"
                  :label="$t('scheduled-tournament.filters.label.buy_in')">
              </filter-input-with-operator>
            </div>

            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="prizePoolSum"
                  :label="$t('scheduled-tournament.filters.label.prize_pool_sum')">
              </filter-input-with-operator>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.maxPlayers"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="requiredNumberOfPlayers"
                  :label="$t('tournament.filters.label.required_number_of_players')"
                  :placeholder="$t('tournament.filters.placeholder.required_number_of_players')">
              </filter-select>
            </div>
          </div> <!-- #row -->

          <div class="row">
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.category"
                  :multiple="true"
                  options-label="label"
                  options-track-by="id"
                  :operator="'in'"
                  filter-name="category"
                  :label="$t('scheduled-tournament.filters.label.question_category')"
                  :placeholder="$t('scheduled-tournament.filters.placeholder.question_category')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="created_at"
                  open-direction="left"
                  :label="$t('filters.label.scheduled-tournament_created_at')">
              </filter-date-range-picker>
            </div>
          </div> <!-- #row -->
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>
    <page-card>
      <template v-slot:header>
        {{ $t('scheduled-tournament.title.list') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

          <div class="table-responsive">
            <b-table striped
                     :empty-text="$t('scheduled-tournament.table.no_results')"
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(id)="data">
                {{ data.item.id | shortId }}
              </template>

              <template #cell(walletCurrency)="data">
                {{ $t(data.value) }}
              </template>

              <template #cell(lobbyId)="data">
                {{ $t(data.value) }}
              </template>

              <template #cell(questionCategoryLabel)="data">
                {{ data.value ? $t('category.'+data.value) : '' }}
              </template>

              <template #cell(rankingPoints)="data">
                <span v-if="data.item.quizRankingPoints.length">
                  {{ data.item.quizRankingPoints ? data.item.quizRankingPoints[0].points : '' }}
                </span>

              </template>

              <template #cell(createdAt)="data">
                {{ data.value | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(dateStart)="data">
                {{ data.value | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(registrationStart)="data">
                {{ data.value | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(registrationEnd)="data">
                {{ data.value | dateTime24HoursUTCFormat }}
              </template>

              <template #cell(prizePool)="data">
                {{ data.value | numberFormat }}
              </template>

              <template #cell(buyIn)="data">
                {{ data.value | numberFormat }}
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <b-button variant="success" size="sm" @click="showItem(data.item)">{{$t('scheduled-tournament.table.btn.details')}}</b-button>
                  <b-button variant="primary" size="sm" @click="duplicateItem(data.item)">{{$t('scheduled-tournament.table.btn.duplicate')}}</b-button>
                  <b-button variant="warning" size="sm" @click="kickAllPlayers(data.item.id)">{{$t('table.btn.kick_all_players')}}</b-button>
                  <b-button variant="danger" v-if="data.item.status !== 'DELETED'" size="sm" @click="deleteItem(data.item.id)">{{$t('scheduled-tournament.table.btn.delete')}}</b-button>
                </div>
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="primary" v-if="data.item.status === 'WAITING_FOR_PLAYERS'">{{$t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`)}}</b-badge>
                <b-badge pill variant="success" v-if="data.item.status === 'WAITING_FOR_START'">{{$t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`)}}</b-badge>
                <b-badge pill variant="warning" v-if="data.item.status === 'IN_PROGRESS'">{{ $t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`) }}</b-badge>
                <b-badge pill variant="dark" v-if="data.item.status === 'FINISHED'">{{ $t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`) }}</b-badge>
                <b-badge pill variant="info" v-if="data.item.status === 'COMPLETED'">{{ $t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`) }}</b-badge>
                <b-badge pill variant="danger" v-if="data.item.status === 'DELETED'">{{$t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`)}}</b-badge>
                <b-badge pill variant="secondary" v-if="data.item.status === 'CANCELED'">{{$t(`scheduled-tournament.status.${data.item.status.toLowerCase()}`)}}</b-badge>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('scheduled-tournament.state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

        </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>

  </div>

</template>


<script>
import axios from 'axios';
import ShowModal from "./components/ShowModal";
import CreateModal from "./components/CreateModal";
import GridMixin from '../../components/grid/GridMixin';
import FilterInput from "../../components/grid/filters/FilterInput";
import FilterInputWithOperator from "../../components/grid/filters/FilterInputWithOperator";
import FilterSelect from "../../components/grid/filters/FilterSelect";
import FilterDateRangePicker from "../../components/grid/filters/FilterDateRangePicker";
import GridPagination from "../../components/grid/grid-pagination/GridPagination";
import Vue from "vue";
import PageHeader from "../../components/page/PageHeader";
import PageCard from "../../components/page/PageCard";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    // eslint-disable-next-line vue/no-unused-components
    CreateModal,
    FilterInput,
    FilterInputWithOperator,
    FilterSelect,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/tournament-waiting-room/scheduled',
      categoryGetUrl: '/api/question/question-category',
      orderColumn: 'status',
      orderDirection: false,
      filterOptions: {
        lobbyId: [
          {value: 'INSTANT', text: this.$t('scheduled-tournament.options.instant')},
          {value: 'SIT_AND_GO', text: this.$t('scheduled-tournament.options.sit_and_go')}
        ],
        status: [
          {value: 'WAITING_FOR_PLAYERS', text: this.$t('scheduled-tournament.options.waiting_for_players')},
          {value: 'WAITING_FOR_START', text: this.$t('scheduled-tournament.options.waiting_for_start')},
          {value: 'IN_PROGRESS', text: this.$t('scheduled-tournament.options.in_progress')},
          {value: 'FINISHED', text: this.$t('scheduled-tournament.options.finished')},
          {value: 'COMPLETED', text: this.$t('scheduled-tournament.options.completed')},
          {value: 'CANCELED', text: this.$t('scheduled-tournament.options.canceled')},
          {value: 'DELETED', text: this.$t('scheduled-tournament.options.deleted')}
        ],
        walletCurrency: [
          {value: 'FUN_COINS', text: this.$t('scheduled-tournament.fun_coins')},
          {value: 'QUIZ_COINS', text: this.$t('scheduled-tournament.quiz_coins')}
        ],
        maxPlayers: [
          {value: '8', text: '8'},
          {value: '16', text: '16'},
          {value: '32', text: '32'},
          {value: '64', text: '64'},
          {value: '128', text: '128'}
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        category: [],
      },
      fields: [
        {key: 'id', sortable: true, label: this.$t('scheduled-tournament.table.header.id')},
        {key: 'name', label: this.$t('scheduled-tournament.table.header.name'), sortable: true},
        {key: 'walletCurrency', label: this.$t('scheduled-tournament.table.header.wallet_currency'), sortable: true},
        {key: 'buyIn', sortable: true, label: this.$t('scheduled-tournament.table.header.buy_in'),},
        {key: 'prizePoolSum', sortable: true, label: this.$t('scheduled-tournament.table.header.prize_pool_sum'),},
        {key: 'awardWinningPositions', sortable: false, label: this.$t('scheduled-tournament.table.header.award_winning_positions'),},
        {key: 'requiredNumberOfPlayers', sortable: true, label: this.$t('scheduled-tournament.table.header.max_players'),},
        {key: 'rankingPoints', sortable: false, label: this.$t('scheduled-tournament.table.header.ranking_points_main_win'),},
        {key: 'questionCategoryLabel', sortable: true, label: this.$t('scheduled-tournament.table.header.question_category'),},
        {key: 'password', sortable: false, label: this.$t('scheduled-tournament.table.header.password'),},
        {key: 'status', sortable: true, label: this.$t('scheduled-tournament.table.header.status')},
        {key: 'createdAt', sortable: true, label: this.$t('scheduled-tournament.table.header.created_at'),},
        {key: 'dateStart', sortable: true, label: this.$t('scheduled-tournament.table.header.date_start'),},
        {key: 'registrationStart', sortable: true, label: this.$t('scheduled-tournament.table.header.registration_start'),},
        {key: 'registrationEnd', sortable: true, label: this.$t('scheduled-tournament.table.header.registration_end'),},
        {key: 'actions', label: this.$t('scheduled-tournament.table.header.actions'), sortable: false}
      ],
      createModalInstance: null,
      showModalInstance: null,
    };
  },
  mounted() {
    if(this.$route.params && this.$route.params.form) {
      this.openModal('create');
    }

    this.getQuestionCategories();
    this.getData(this.categoryGetUrl+'?limit=1000', ['filterOptions', 'category']);
    this.getData('/api/lobby/configuration', ['filterOptions', 'lobbyId']);
  },
  computed: {
    walletCurrency: function () {
      return this.$store.getters["root/walletCurrency"].map(d => {
        return {
          value: d.value,
          text: this.$t(d.value)
        }
      });
    },
  },
  methods: {
    getQuestionCategories() {
      axios.get('/api/question/question-category')
        .then((resp) => {
          this.filterOptions.category = resp.data.items.map(c => {
            return {
              id: c.id,
              label: this.$t('category.'+c.name)
            }
          });
        })
        .catch((err) => {
          console.log(err)
        });
    },
    getData: function (url, localStorage) {
      let _that = this;
      axios.get(url)
          .then((resp) => {
            if (localStorage[1] === 'lobbyId') {
              _that[localStorage[0]][localStorage[1]] = resp.data.items.map(d => {
                return {
                  label: this.$t(d.id),
                  value: d.id
                }
              });
            } else if (localStorage[1] === 'category') {
              _that[localStorage[0]][localStorage[1]] = resp.data.items.map(c => {
                return {
                  id: c.id,
                  label: this.$t('category.'+c.name)
                }
              });
            } else {
              _that[localStorage[0]][localStorage[1]] = resp.data.items;
            }

          })
          .catch((err) => {
            console.log(err)
          })
    },
    deleteItem(id) {
      let _that = this;
      this.$bvModal.msgBoxConfirm('Operation needs confirmation')
          .then(value => {
            if (value) {
              axios.delete('/api/waiting-room/' + id)
                  .then(function () {
                    _that.isBusy = true;
                    setTimeout(() => _that.loadItems(), 2000);
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    duplicateItem(data) {
      data.setNewRankingPoints = false;

      this.openModal('create');
      this.createModalInstance.setData(data);
    },
    kickAllPlayers(id) {
      let _that = this;
      this.$bvModal.msgBoxConfirm(this.$t('waiting_room.kick_all_players_title'))
          .then(value => {
            if (value) {
              axios.post(`/api/waiting-room/${id}/kick-all`)
                  .then(function () {
                    _that.isBusy = true;
                    setTimeout(() => _that.loadItems(), 2000);
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    showItem(data) {
      this.openModal('show');
      this.showModalInstance.setData(data);
    },
    openModal(modalName) {
      let ModalConstructor;
      switch(modalName){
        case('create'): {
          if(!this.createModalInstance){
            ModalConstructor = Vue.extend(CreateModal)
            this.createModalInstance = new ModalConstructor({parent: this});
            this.createModalInstance.lobbies = this.filterOptions.lobbyId.map(d => {
              return { id: d.value, label: this.$t(d.value)}
            });
            this.createModalInstance.$mount();
          } else {
            this.createModalInstance.showModal();
          }
          break;
        }
        case('show'): {
          if(!this.showModalInstance){
            ModalConstructor = Vue.extend(ShowModal)
            this.showModalInstance = new ModalConstructor({parent: this});
            this.showModalInstance.$mount();
          } else {
            this.showModalInstance.showModal();
          }
          break;
        }
      }
    }

  }
}
</script>
